/**
 * Fresh Backup Meals standing orders — `/api/school-orders/back-up-meals`.
 *
 * A campus's backup meals are ONE whole-school weekly standing order per
 * mealtime (a line-scoped SCHOOL_FORECAST subscription). `GET` returns the
 * picture per published BACK_UP menu — live items, weekdays, per-item
 * quantities — and `PUT` replaces one mealtime's picture from the dialog's
 * tile quantities (all-zero archives the standing order).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { Mealtime } from '@/lib/mealtime'

import { apiFetch } from './http'
import type { BackUpMenuItemSummary } from './school-orders'

/** 0..6, Sun..Sat — the runner's `deliveryDays` bit positions. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6

export interface BackUpStandingOrderWire {
  mealtime: Mealtime
  menuId: string
  /** YYYY-MM-DD, the published menu's window. */
  startDate: string
  endDate: string
  items: BackUpMenuItemSummary[]
  /** Weekdays the standing order fires; empty when none exists. */
  weekdays: Weekday[]
  /** Standing quantity per menuItemId (absent = 0). */
  quantities: Record<string, number>
  /** Non-null when a standing order exists for this mealtime. */
  subscription: { id: string; rrule: string; studentCount: number } | null
}

export interface BackUpStandingOrdersResponse {
  standingOrders: BackUpStandingOrderWire[]
}

export interface SaveBackUpStandingOrderInput {
  schoolLocationId: string
  mealtime: Mealtime
  menuId: string
  weekdays: Weekday[]
  items: Array<{ menuItemId: string; quantity: number }>
}

export function backUpMealsQueryKey(schoolLocationId: string) {
  return ['school-orders', 'back-up-meals', schoolLocationId] as const
}

export async function fetchBackUpStandingOrders(
  schoolLocationId: string,
  signal?: AbortSignal,
): Promise<BackUpStandingOrdersResponse> {
  return apiFetch<BackUpStandingOrdersResponse>('/api/school-orders/back-up-meals', {
    query: { schoolLocationId },
    signal,
  })
}

export async function saveBackUpStandingOrder(
  input: SaveBackUpStandingOrderInput,
): Promise<{ standingOrder: BackUpStandingOrderWire }> {
  return apiFetch<{ standingOrder: BackUpStandingOrderWire }>('/api/school-orders/back-up-meals', {
    method: 'PUT',
    body: input,
  })
}

export function useBackUpStandingOrdersQuery(schoolLocationId: string, opts?: { enabled?: boolean }) {
  return useQuery<BackUpStandingOrdersResponse>({
    queryKey: backUpMealsQueryKey(schoolLocationId),
    queryFn: ({ signal }) => fetchBackUpStandingOrders(schoolLocationId, signal),
    enabled: opts?.enabled ?? Boolean(schoolLocationId),
  })
}

export function useSaveBackUpStandingOrderMutation(schoolLocationId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: saveBackUpStandingOrder,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: backUpMealsQueryKey(schoolLocationId) })
      // The runner materializes the standing order onto the week's batches —
      // the week cards and month calendar read those.
      void qc.invalidateQueries({ queryKey: ['school-orders', 'forecast-batches'] })
    },
  })
}
